<template>
	<v-form @submit.prevent :disabled="disableActions || submitLoading">
		<!-- confirm acceptance -->
		<v-dialog v-model="losingAlertDialog" max-width="400" :persistent="submitLoading">
			<v-card>
				<v-card-title class="justify-center text-capitalize alert-color--text">
					<v-btn :disabled="submitLoading" @click="losingAlertDialog = false" icon>
						<v-icon>$close</v-icon>
					</v-btn>
					<v-spacer/>
					<span class="me-8">
						{{$T('confirm-acceptance')}}
					</span>
					<v-spacer/>
				</v-card-title>
				<v-card-text class="pb-0">
					<p class="text-subtitle-1 mb-0">
						{{$T('losing-purchase-alert')}}
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn
						type="submit"
						color="alert-color white--text"
						:loading="submitLoading"
						@click="submitAcceptance(true)"
					>
						{{$T('accept')}}
					</v-btn>
					<v-btn @click="losingAlertDialog = false" :disabled="submitLoading">
						{{$T('close')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- fixed data -->
		<v-row dense align="center" class="ma-0 mb-2 px-1">
			<template v-if="!isExperimentalSimulation">
				<!-- date -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<v-text-field
						v-model="date"
						:label="$T('inputs.date')"
						outlined
						readonly
						dense
						hide-details
						:loading="loading"
					/>
				</v-col>
				
				<!-- provider -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<v-autocomplete
						v-model="providerId"
						:items="accounts"
						item-text="name"
						item-value="id"
						:label="$T('inputs.provider')"
						outlined
						readonly
						dense
						hide-details
						:loading="loading"
					/>
				</v-col>
				
				<!-- entry-no -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<v-text-field
						v-model="entryNo"
						:label="$T('inputs.entry-no')"
						outlined
						readonly
						dense
						hide-details
						:loading="loading"
					/>
				</v-col>
				
				<!-- owner -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<v-autocomplete
						v-model="ownerId"
						:label="$T('inputs.owner')"
						:items="[{id: null, name: 'Ugarit', type: 1 }, ...owners]"
						item-text="name"
						item-value="id"
						outlined
						dense
						hide-details
						:readonly="disableActions"
						:loading="loading"
					/>
				</v-col>
			</template>

			<v-spacer/>
			
			<v-col
				cols="auto"
				class="text-body-1 font-weight-bold"
				:class="[
					totalPercent === 100 ? 'primary--text' : 'red--text',
					{'shake': totalPercent !== 100}
				]"
			>
				{{totalPercent}}%
				<v-icon v-if="totalPercent !== 100" color="red">$alert</v-icon>
			</v-col>

			<v-spacer/>

			<v-btn
				v-if="!isExperimentalSimulation"
				class="px-2"
				color="bgColor"
				:to="{ name: 'purchases' }"
			>
				<v-icon size="20" class="me-1">$back</v-icon>
				{{$T('back')}}
			</v-btn>
		</v-row>

		<!-- changeable -->
		<v-row dense align="center" class="outline-container bgColor ma-0 mb-2">
			<!-- exchange rate -->
			<v-col lg="3" md="4" sm="6" cols="12">
				<v-text-field
					:label="$T('inputs.exchange-rate')"
					outlined
					dense
					hide-details
					:value="exchangeRateFormat"
					@input="
						exchangeRate =
							typeof $event === 'string'
								? +$options.filters.removeNumberFormat($event)
								: +$event;
						exchangeRateFormat = $options.filters.numberFormat($event);
						updateTableFields();
					"
				/>
			</v-col>
			
			<!-- expenses -->
			<v-col lg="3" md="4" sm="6" cols="12">
				<v-text-field
					:label="$T('inputs.expenses-cf')"
					outlined
					dense
					hide-details
					:value="expensesFormat"
					@input="
						expenses =
							typeof $event === 'string'
								? +$options.filters.removeNumberFormat($event)
								: +$event;
						expensesFormat = $options.filters.numberFormat($event);
						updateTableFields()
					"
				/>
			</v-col>
			
			<!-- tax percent -->
			<v-col lg="3" md="4" sm="6" cols="12">
				<v-text-field
					v-model.number="taxPercent"
					:label="$T('inputs.tax-percent')"
					type="number"
					min="0"
					hide-spin-buttons
					outlined
					dense
					hide-details
					@input="updateTableFields()"
				/>
			</v-col>
			
			<!-- px cal -->
			<v-col lg="3" md="4" sm="6" cols="12">
				<v-text-field
					:label="$T('inputs.px-cal')"
					outlined
					dense
					hide-details
					:value="pxCalFormat"
					@input="
						pxCal =
							typeof $event === 'string'
								? +$options.filters.removeNumberFormat($event)
								: +$event;
						pxCalFormat = $options.filters.numberFormat($event);
						updateTableFields();
					"
				/>
			</v-col>
		</v-row>

		<!-- table -->
		<bee-handy-smart-table
			:headers="headers"
			:items="loading ? [] : entries.filter(c => (!purchaseData.isSeed && c.itemCategory === itemCategory.commercial) ? true : (c.id === seedsId && c.itemCategory === itemCategory.commercial))"
			fixed-header
			:loading="loading"
			:height="$vuetify.breakpoint.height - 235"
			:items-per-page="-1"
			:data-table-props="{ disableSort: true }"
			:item-class="item => item.parentId ? 'grey lighten-4' : null"
			hot-inputs
			dense
			hide-inputs-details
			hide-default-footer
		>
			<!-- item name -->
			<template v-slot:item.itemName="{ item }">
				<span :class="{'ms-6': item.parentId}">
					{{item.itemName}}
				</span>
				<!-- <span v-if="item.hasParent" class="ms-1 text-caption grey--text">({{item.hasParent}})</span> -->
			</template>
			
			<!-- percent -->
			<template v-slot:item.percent="{ item, index }">
				<div class="d-flex align-center">
					<v-text-field
						v-model.number="item.percent"
						type="number"
						min="0"
						hide-spin-buttons
						dense
						hide-details
						class="mb-4"
						:class="{'ms-4': item.parentId}"
						:error="
							item.parentId
								? getTotalChildrenPercent(entries, item.parentId) !== 100
								: false
						"
						:rules="rules.requiredAsPercent"
						@input="updateTableFields(index)"
						@keydown.tab="
							item.id === entries[entries.length - 1].id
								? (() => { $event.preventDefault(); $refs.salePriceInput1.focus(); })()
								: null
						"
					/>
					<div
						v-if="item.hasChildren"
						class="text-caption ms-6"
						:class="{'alert-color--text': getTotalChildrenPercent(entries, item.id) !== 100}"
					>
						({{getTotalChildrenPercent(entries, item.id)}}%)
					</div>
				</div>
			</template>

			<!-- salePrice -->
			<template v-slot:item.salePrice="{ item, index }">
				<v-text-field
					v-if="!item.hasChildren"
					:ref="`salePriceInput${index}`"
					:tabindex="!purchaseData.isSeed ? 1 : null"
					dense
					hide-details
					class="mb-4"
					:value="item.formattedSalePrice"
					@input="
						item.salePrice =
							typeof $event === 'string'
								? +$options.filters.removeNumberFormat($event)
								: +$event;
						item.formattedSalePrice = $options.filters.numberFormat($event);
						updateTableFields(index);
					"
				/>
				<span v-else>{{ $options.filters.numberFormat(item.salePrice) }}</span>
			</template>

			<!-- item utility -->
			<template v-slot:item.utility="{ item, header, getShownValueInCell }">
				<span
					class="font-weight-bold"
					:class="!item.parentId ? (item.utility >= 0 ? 'primary--text' : 'red--text') : null"
				>
					{{!item.parentId ? getShownValueInCell({item, header}) : '-'}}
				</span>
			</template>
		</bee-handy-smart-table>

		<!-- footer -->
		<v-footer app inset elevation="10" height="60" class="grey lighten-2">
			<v-progress-linear absolute top v-if="submitLoading && !losingAlertDialog" indeterminate/>

			{{$T('total-benefit-for-kg')}}:
			<v-chip
				:color="totalBenefits > 0 ? 'primary' : 'red'"
				class="ms-2 text-h6 font-weight-bold white--text"
			>
				<!-- :class="totalBenefits > 0 ? 'primary--text' : 'red--text'" -->
				{{totalBenefits}} $
			</v-chip>

			<template v-if="!disableActions">
				<v-spacer/>
				
				<template v-if="!isExperimentalSimulation">
					<v-btn
						:tabindex="!purchaseData.isSeed ? 1 : null"
						:disabled="submitLoading"
						color="primary"
						class="me-2"
						@click="totalBenefits > 0 ? submitAcceptance(true) : losingAlertDialog = true;"
					>{{$T('accept')}}</v-btn>
					<v-btn
						:tabindex="!purchaseData.isSeed ? 1 : null"
						:disabled="submitLoading"
						color="alert-color white--text"
						@click="submitAcceptance(false)"
					>{{$T('reject')}}</v-btn>
				</template>
				<v-btn
					v-else
					:disabled="submitLoading"
					color="primary"
					class="me-2"
					@click="submitExperimentalSimulation()"
				>{{$T('save')}}</v-btn>
			</template>
		</v-footer>
	</v-form>
</template>

<script>
import moment from 'moment'
import rules from '@/helpers/validation-rules'
import { fixNumber, isEmptyId } from '@/helpers/functions'
import { mapState } from 'vuex'
import { type, itemCategory } from '@/helpers/enums'
export default {
	name: 'PurchaseSimulation',

	data() {
		return {
			rules,
			type,

			loading: false,
			submitLoading: false,
			disableActions: true,
			purchaseData: {},

			losingAlertDialog: false,

			date: null,
			providerId: null,
			entryNo: null,
			ownerId: null,

			// data
			/* exchangeRate: 7.75,
			expenses: 1.5,
			taxPercent: 5.26,
			pxCal: 2000, */
			exchangeRate: null,
			expenses: null,
			taxPercent: null,
			pxCal: 0,

			entries: [],
			itemCategory,
			pxCalFormat: null,
			expensesFormat: null,
			exchangeRateFormat: null
		}
	},

	watch: {
		'$route.name'(val) {
			if (val === 'experimental-simulation') { this.fetchExperimentalSimulation(); }
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			accounts: state => state.accounts.accounts.filter(c => !c.isArchived),
			cardamomRootId: state => state.configs.itemsConfigs.cardamomRootId,
			owners: state => state.configs.configsTrees.owners,
			items: state => state.items.items,
			itemsTree: state => state.items.itemsTree,
			crushId: state => state.configs.itemsConfigs.crushId,
			seedsId: state => state.configs.itemsConfigs.seedsId,
		}),

		headers() {
			return [
				{
					text: this.$T('headers.item'),
					name: 'itemName',
					value: 'itemName',
					columnClass: 'px-2',
					noNotAvailableText: true,
					width: 100
				},
				{
					text: '%',
					name: 'percent',
					value: 'percent',
					columnClass: 'px-2',
					type: 'number',
					noNotAvailableText: true,
					width: 120,
				},
				/* {
					text: this.$T('headers.weight'),
					name: 'weight',
					value: 'weight',
					columnClass: 'px-2',
					noNotAvailableText: true,
				}, */
				{
					text: this.$T('headers.purchase-cost'),
					name: 'purchaseCost',
					value: 'purchaseCost',
					type: 'number-format',
					align: 'center',
					width: '20%',
					noNotAvailableText: true,
					setValueExpr: val => val + ' $'
				},
				{
					text: this.$T('headers.cf-cost'),
					name: 'cfCost',
					value: 'cfCost',
					type: 'number-format',
					align: 'center',
					width: '20%',
					noNotAvailableText: true,
					setValueExpr: val => val + ' $'
				},
				{
					text: this.$T('headers.sale-price') + ' ($)',
					name: 'salePrice',
					value: 'salePrice',
					type: 'number-format',
					width: 120,
					noNotAvailableText: true,
				},
				{
					text: this.$T('headers.utility'),
					name: 'utility',
					value: 'utility',
					align: 'center',
					type: 'number-format',
					noNotAvailableText: true,
					setValueExpr: val => val + ' $'
				},
			]
		},
		totalPercent() {
			return this.entries.filter(c => !c.parentId).map(c => c.percent).reduce((a, b) => +a + b, 0)
		},
		totalBenefits() {
			return this.$options.filters.numberFormat(
				fixNumber(
					this.entries.filter(c => !c.parentId)
						.map(c => (c.percent / 100) * c.utility).reduce((a, b) => a + b, 0)
				)
			);
		},

		isExperimentalSimulation() { return this.$route.name === 'experimental-simulation'; }
	},

	methods: {
		updateTableFields(index) {
			const isCrush = (index) => {
				return this.entries[index].id === this.crushId;
				/* return (this.entries[index].itemName.toLowerCase() === 'crush' || index === this.entries.length - 1) */
			}
			const updateEntry = (index) => {
				const pxCal = this.pxCal || 0;
				const exchangeRate = this.exchangeRate || 1;
				const taxPercent = this.taxPercent || 0;
				const expenses = this.expenses || 0;
				const salePrice = this.entries[index].salePrice || 0;
				this.entries[index].formattedSalePrice = this.$options.filters.numberFormat(salePrice);
				const percent = this.entries[index].percent || 0;

				const crushPercent = (
					this.entries.find(c => c.id === this.crushId)
				)?.percent ?? 0;

				this.entries[index].weight = 0;
				// this.entries[index].weight = fixNumber((this.entries[index].percent / 100) * this.weight);
				// this.entries[index].purchaseCost = this.entries[index].weight % 7;
				
				this.entries[index].purchaseCost = fixNumber(
					(pxCal / 46 / exchangeRate) /
					(1 - (taxPercent / 100)) /
					(1 - (crushPercent / 100))
				);

				console.log('purchaseCost', this.entries[index].purchaseCost);
				this.entries[index].cfCost = expenses + this.entries[index].purchaseCost;
				this.entries[index].totalCost = this.entries[index].salePrice * this.entries[index].weight;
				this.entries[index].utility =
					percent && !isCrush(index) ? salePrice - this.entries[index].cfCost : 0;
				// this.entries[index].utility = salePrice - this.entries[index].cfCost;
			}

			if (index !== undefined) {
				updateEntry(index);
				
				// update parents
				const parentIndex = this.entries.findIndex(c => c.id === this.entries[index].parentId);
				if (parentIndex !== -1) {
					const brothers =
						this.entries.filter(c => c.parentId === this.entries[index].parentId);
					this.entries[parentIndex].salePrice =
						fixNumber(
							brothers.reduce((res, a) =>
								a.percent ? res + ((a.percent / 100) * a.salePrice) : res
							, 0)
						);
					this.entries[parentIndex].formattedSalePrice =
						this.$options.filters.numberFormat(this.entries[parentIndex].salePrice || 0);
					this.entries[parentIndex].totalCost =
						brothers.reduce((res, a) => res + a.totalCost, 0);
					this.entries[parentIndex].utility = 
						this.entries[parentIndex].salePrice
							? this.entries[parentIndex].salePrice - this.entries[parentIndex].cfCost
							: 0;
				}
			} else {
				for (let i = 0; i < this.entries.length; i++) {
					updateEntry(i);
				}
			}
		},

		updatePurchaseEntires() {
			this.entries = [];
			if (!this.items.length) return;
			const subTree = this.itemsTree.find(c => c.id === (this.cardamomRootId || this.items[0].id));

			subTree.children.forEach(c => {
				this.entries.push({
					id: c.id,
					itemName: c.name,
					parentId: null,
					hasChildren: Boolean(c.children && c.children.length > 0),
					weight: 0,
					percent: 0,
					purchaseCost: 0,
					cfCost: 0,
					salePrice: 0,
					formattedSalePrice: '',
					totalCost: 0,
					utility: 0,
					itemCategory: c.itemCategory
				})
				if (!c.children.length) return;

				c.children.forEach(e => {
					this.entries.push({
						id: e.id,
						itemName: e.name,
						parentId: c.id,
						hasChildren: false,
						weight: 0,
						percent: 0,
						purchaseCost: 0,
						cfCost: 0,
						salePrice: 0,
						formattedSalePrice: '',
						totalCost: 0,
						utility: 0,
						itemCategory: c.itemCategory
					})
				})
			})
		},

		getTotalChildrenPercent(entries, parentId) {
			return entries.filter(c => c.parentId === parentId).reduce((res, a) => res + +a.percent, 0);
		},

		// simulations
		fillSimulationData(data) {
			this.providerId = data.providerId;
			this.entryNo = data.entryNo;
			this.ownerId = data.ownerId;
			// this.weight = data.weight;
			
			this.updatePurchaseEntires();
			this.entries.forEach((entry, index) => {
				const item = data.items && data.items.find(c => c.itemId === entry.id);
				if (item) {
					this.entries[index].percent = item.percent;
					// this.entries[index].weight = (item.percent / 100) * this.weight;
					this.entries[index].weight = (item.percent / 100) * 0;
					this.entries[index].salePrice = item.salePrice || 0;
					this.entries[index].formattedSalePrice =
						this.$options.filters.numberFormat(item.salePrice || 0);
				}
				/* else {
					this.entries.splice(index, 1);
				} */
			})
			this.updateTableFields();
		},

		fetchExperimentalSimulation(isFirstLoading = false) {
			this.loading = true;
			return this.$store.dispatch('purchasesSimulation/fetchTestingSimulation').then((data) => {
				this.disableActions = false;
				
				this.date = moment().format('YYYY-MM-DD');
				this.exchangeRate = data.exchangeRate;
				this.exchangeRateFormat = this.$options.filters.numberFormat(data.exchangeRate)
				this.expenses = data.expenses;
				this.expensesFormat = this.$options.filters.numberFormat(data.expenses)
				this.taxPercent = data.spaceBill;
				this.pxCal = data.pxCal !== null ? data.pxCal : 0;
				this.pxCalFormat = data.pxCal !== null ? this.$options.filters.numberFormat(data.pxCal) : 0

				this.fillSimulationData(data);
			}).finally(() => {
				if (!isFirstLoading) { this.loading = false; }
			})
		},

		// submit
		isValidSimulation() {
			if (this.totalPercent !== 100) return false;
			if (!this.purchaseData.isSeed) {
				const mainItemsIds = this.entries.filter(c => c.hasChildren).map(c => c.id);
				const subItems = this.entries.filter(c => !c.hasChildren);
				for (let i = 0; i < mainItemsIds.length; i++) {
					const id = mainItemsIds[i];
					const totalPercent =
						subItems.filter(c => c.parentId === id).reduce((sum, a) => sum + a.percent, 0);
					if (totalPercent !== 100) return false;
				}
			}
			return true;
		},

		saveSimulation() {
			const actionName =
				!this.$route.query.simulationId || this.isExperimentalSimulation ? 'create' : 'update';
			return this.$store.dispatch(`purchasesSimulation/${actionName}`, {
				id: this.$route.query.simulationId,
				purchaseId: !this.isExperimentalSimulation ? this.$route.params.id : null,
				date: this.date,
				providerId: this.providerId,
				entryNo: this.entryNo,
				weight: null,
				ownerId: this.ownerId,
				exchangeRate: this.exchangeRate,
				expenses: this.expenses,
				spaceBill: this.taxPercent,
				pxCal: this.pxCal,
				simulationItems: this.entries.map(c => ({
					itemId: c.id,
					percent: c.percent,
					weight: null,
					salePrice: c.salePrice
				}))
			})
		},
		
		submitAcceptance(isAccept) {
			if (!this.isValidSimulation()) return;

			this.submitLoading = true;
			Promise.all([
				this.saveSimulation(),
				this.$store.dispatch('purchasesSimulation/updatePurchaseState', {
					id: this.$route.params.id,
					administrationPrice: this.pxCal,
					administrationAcceptance: isAccept,
					farmerAcceptance: this.purchaseData.farmerAcceptance,
				})
			]).then(() => {
				this.statusDialog = false;
				this.$eventBus.$emit('show-snackbar', this.$T('messages.purchase-state-updated'), 'info');
				this.$router.push({ name: 'purchases' });
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		submitExperimentalSimulation(isBackgroundSubmit = false) {
			if (this.isValidSimulation()) {
				this.submitLoading = true;
				this.saveSimulation().then(() => {
					if (!isBackgroundSubmit) {
						this.$eventBus.$emit('show-snackbar', this.$T('messages.experimental-simulation-saved'), 'info');
					}
					// remove draft
					this.$eStorage.l.remove('experimental-simulation');
				}).finally(() => {
					this.submitLoading = false;
				});
			} else {
				// save draft
				this.$eStorage.l.set('experimental-simulation', {
					exchangeRate: this.exchangeRate,
					expenses: this.expenses,
					taxPercent: this.taxPercent,
					pxCal: this.pxCal,
					entries: this.entries.map(c => ({
						id: c.id,
						percent: c.percent,
						salePrice: c.salePrice
					}))
				});
			}
		}
	},

	async created() {
		if (this.items.length) {
			this.loading = true;

			let hasSimulation = false;
			// fetch purchase simulation and fill data
			if (!this.isExperimentalSimulation) {
				await this.$store.dispatch('purchasesSimulation/fetchPurchaseSimulation', {
					purchaseId: this.$route.params.id
				}).then((data) => {
					hasSimulation = !isEmptyId(data.id);
					if (hasSimulation) {
						this.date = moment(data.date).format('YYYY-MM-DD');
						this.exchangeRate = data.exchangeRate;
						this.exchangeRateFormat = this.$options.filters.numberFormat(data.exchangeRate)
						this.expenses = data.expenses;
						this.expensesFormat = this.$options.filters.numberFormat(data.expenses)
						this.taxPercent = data.spaceBill;
						this.pxCal = data.pxCal;
						this.pxCalFormat = this.$options.filters.numberFormat(data.pxCal) 

						this.fillSimulationData(data);
						if (this.$route.query.simulationId !== data.id) {
							this.$router.replace({
								name: 'purchase-simulation',
								params: { id: this.$route.params.id },
								query: { simulationId: data.id }
							})
						}
					}
				});

				// fetch purchase and fill data
				await this.$store.dispatch('purchases/fetchById', {
					id: this.$route.params.id
				}).then(data => {
					this.purchaseData = data;
					this.date = moment().format('YYYY-MM-DD');
					this.disableActions = data.administrationAcceptance && data.farmerAcceptance;
					if (!hasSimulation) this.fillSimulationData(data);
				})
				
				if (!hasSimulation) {
					await this.$store.dispatch('purchasesSimulation/fetchLatestSimulation').then(data => {
						this.exchangeRate = data.exchangeRate;
						this.exchangeRateFormat = this.$options.filters.numberFormat(data.exchangeRate)
						this.expenses = data.expenses;
						this.expensesFormat = this.$options.filters.numberFormat(data.expenses)
						this.taxPercent = data.spaceBill;
						this.pxCal = data.pxCal;
						this.pxCalFormat = this.$options.filters.numberFormat(data.pxCal)

						this.entries.forEach((entry, index) => {
							const item = data.items && data.items.find(c => c.itemId === entry.id);
							if (item) {
								this.entries[index].salePrice = item.salePrice;
								this.entries[index].formattedSalePrice =
									this.$options.filters.numberFormat(item.salePrice || 0);
								
								if (this.entries[index].parentId) {
									this.entries[index].percent = item.percent
								}
							}
						});
						this.updateTableFields();
					})
				}
			} else {
				/* experimental-simulation */
				// fill from API
				if (!this.$eStorage.l.has('experimental-simulation')) {
					await this.fetchExperimentalSimulation(true);
				// fill from Draft
				} else {
					this.updatePurchaseEntires();
					const experimentalSimulation = this.$eStorage.l.get('experimental-simulation')
					
					this.date = moment().format('YYYY-MM-DD');
					this.exchangeRate = experimentalSimulation.exchangeRate;
					this.exchangeRateFormat = this.$options.filters.numberFormat(experimentalSimulation.exchangeRate)
					this.expenses = experimentalSimulation.expenses;
					this.expensesFormat = this.$options.filters.numberFormat(experimentalSimulation.expenses)
					this.taxPercent = experimentalSimulation.taxPercent;
					this.pxCal = experimentalSimulation.pxCal;
					this.pxCalFormat = this.$options.filters.numberFormat(experimentalSimulation.pxCal)

					experimentalSimulation.entries.forEach(c => {
						const index = this.entries.findIndex(e => e.id === c.id);
						if (index !== -1) {
							this.entries[index].percent = c.percent;
							this.entries[index].salePrice = c.salePrice;
							this.entries[index].formattedSalePrice =
								this.$options.filters.numberFormat(c.salePrice || 0);
						}
					});
					this.updateTableFields();

					this.disableActions = false;
				}
			}
			
			this.loading = false;
		}
		
		if (
			(this.permissions.purchasesSimulation.isReadonly && this.$route.name === 'purchase-simulation') ||
			(this.permissions.managingExperimentalSimulation.isReadonly && this.$route.name === 'experimental-simulation')
		) { this.disableActions = true; }
	},

	beforeRouteLeave(to, from, next) {
		if (this.isExperimentalSimulation) {
			this.submitExperimentalSimulation(true);
		}
		next();
	}

	/* beforeRouteEnter(to, from, next) {
		next(vm => {
			if (vm.permissions.purchasesSimulation.isNone) {
				next({ name: 'home', replace: true });
			}
			return true
		});
	} */
}
</script>

<style lang="scss">
.shake {
	position: relative;
	&::after {
		content: '';
		display: block;
		position: absolute;
		left: 0;
		bottom: 4px;
		right: 0;
		width: 80%;
		margin: 0 auto;
		height: 2px;
		background: #f44336;

		animation: shake 1s ease-in-out infinite;

		@keyframes shake {
			0% { width: 0; }
			50% { width: 80%; }
			100% { width: 0; }
		}
	}
}
</style>